import { create } from 'zustand';
import type { ChatState, Message, PageInfo, UserInfo } from '../types/chat.types';
import { getOrCreateSessionId } from '../utils/session';

const USER_INFO_STORAGE_KEY = 'web-chat-user-info';

function loadUserInfoFromStorage(): UserInfo {
  try {
    const stored = localStorage.getItem(USER_INFO_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as UserInfo;
    }
  } catch {
    // ignore parse errors
  }
  return {};
}

function saveUserInfoToStorage(userInfo: UserInfo): void {
  try {
    localStorage.setItem(USER_INFO_STORAGE_KEY, JSON.stringify(userInfo));
  } catch {
    // ignore storage errors
  }
}

const initialUserInfo = loadUserInfoFromStorage();

/**
 * Chat store using Zustand
 */
export const useChatStore = create<ChatState>((set) => ({
  // State
  messages: [],
  hasMoreMessages: false,
  loadingMessages: false,
  sendingMessage: false,
  startCursor: undefined,
  endCursor: undefined,
  userInfo: initialUserInfo,
  sessionId: getOrCreateSessionId(),
  isTyping: false,
  streamStage: undefined,
  streamArticles: [],
  justStreamedBot: false,
  chatTitle: undefined,
  titleMessageId: undefined,

  // Actions
  addMessage: (message: Message) => {
    set((state) => {
      // Check if message already exists (prevent duplicates)
      const exists = state.messages.some((m) => m.id === message.id);
      if (exists) {
        return state;
      }

      return {
        messages: [message, ...state.messages],
      };
    });
  },

  replaceMessage: (oldId: string, newMessage: Message) => {
    set((state) => ({
      messages: state.messages.map((m) => (m.id === oldId ? newMessage : m)),
    }));
  },

  removeMessage: (id: string) => {
    set((state) => ({
      messages: state.messages.filter((m) => m.id !== id),
    }));
  },

  addMessages: (messages: Message[], pageInfo: PageInfo) => {
    set((state) => {
      // Filter out duplicates
      const existingIds = new Set(state.messages.map((m) => m.id));
      const newMessages = messages.filter((m) => !existingIds.has(m.id));

      return {
        messages: [...newMessages, ...state.messages],
        hasMoreMessages: pageInfo.hasPreviousPage,
        startCursor: pageInfo.startCursor,
      };
    });
  },

  setMessages: (messages: Message[]) => {
    set({ messages });
  },

  setSendingMessage: (sending: boolean) => {
    set({ sendingMessage: sending });
  },

  setLoadingMessages: (loading: boolean) => {
    set({ loadingMessages: loading });
  },

  setTyping: (typing: boolean) => {
    set({ isTyping: typing });
  },

  setStreamStage: (stage) => {
    set({ streamStage: stage });
  },

  addStreamArticle: (title: string) => {
    set((state) => ({ streamArticles: [...state.streamArticles, title] }));
  },

  clearStream: () => {
    set({ streamStage: undefined, streamArticles: [] });
  },

  markBotStreamed: () => {
    set({ justStreamedBot: true });
  },

  consumeBotStreamed: () => {
    set({ justStreamedBot: false });
  },

  setUserInfo: (info: Partial<UserInfo>) => {
    set((state) => {
      const newUserInfo = { ...state.userInfo, ...info };
      saveUserInfoToStorage(newUserInfo);
      return { userInfo: newUserInfo };
    });
  },

  setSessionId: (sessionId: string) => {
    set({ sessionId });
  },

  setChatTitle: (chatTitle?: string) => {
    set({ chatTitle });
  },

  setTitleForMessage: (title: string, messageId: string) => {
    set({ chatTitle: title, titleMessageId: messageId });
  },

  reset: () => {
    set({
      messages: [],
      hasMoreMessages: false,
      loadingMessages: false,
      sendingMessage: false,
      startCursor: undefined,
      endCursor: undefined,
      isTyping: false,
      streamStage: undefined,
      streamArticles: [],
      justStreamedBot: false,
      chatTitle: undefined,
      titleMessageId: undefined,
    });
  },
}));
